import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../app/api-client';

export const adminNewsKey = ['admin-news'] as const;

export interface AdminNewsRow {
  id: string;
  title: string;
  status: 'PUBLISHED' | 'HIDDEN';
  publishedAt: string;
  cityId: string;
  authorName: string | null;
  sourceUrl: string;
  sourceMessageId: string | null;
  coverStorageKey: string | null;
  sourceChannel: { id: string; slug: string; title: string } | null;
}

export interface AdminNewsListResponse {
  rows: AdminNewsRow[];
  total: number;
}

export interface AdminNewsListFilters {
  status?: 'PUBLISHED' | 'HIDDEN';
  search?: string;
  cityId?: string;
  channelId?: string;
  limit?: number;
  offset?: number;
}

function buildSearchParams(filters: AdminNewsListFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return params;
}

export const adminNewsListQueryOptions = (filters: AdminNewsListFilters = {}) =>
  queryOptions({
    queryKey: [...adminNewsKey, 'list', filters] as const,
    queryFn: () =>
      api.get('admin/news', { searchParams: buildSearchParams(filters) }).json<AdminNewsListResponse>(),
    staleTime: 10_000,
  });

function invalidateList(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: [...adminNewsKey, 'list'] });
}

export function useUpdateNewsStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'PUBLISHED' | 'HIDDEN' }) =>
      api.patch(`admin/news/${id}`, { json: { status } }).json<AdminNewsRow>(),
    onSuccess: () => invalidateList(qc),
  });
}

export function useDeleteNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/news/${id}`),
    onSuccess: () => invalidateList(qc),
  });
}

export function useConvertNewsToEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`admin/news/${id}/convert-to-event`).json<{ eventId: string }>(),
    onSuccess: () => invalidateList(qc),
  });
}
